/**
 * Validates that an event object has the required structure.
 * @param {object} event - The event to validate
 * @returns {boolean} True if the event is valid
 */
export const isValidEvent = (event) => {
  return (
    event &&
    typeof event === "object" &&
    typeof event.log === "string" &&
    typeof event.level === "string" &&
    typeof event.timestamp === "string"
  );
};

/**
 * Formats a timestamp to ISO format without milliseconds.
 * @param {string} timestamp - ISO timestamp string
 * @returns {string} Formatted timestamp
 */
export const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  return date.toISOString().slice(0, 19).replace("T", " ");
};

/**
 * Converts execution time in seconds to a human-readable format.
 * @param {string} log - Log message containing time in seconds
 * @returns {string} Log with formatted time
 */
export const formatExecutionTimeNatural = (log) => {
  const timeRegex = /([a-zA-Z\s]*time[:]?)[^\d]*(\d+(\.\d+)?)\s*seconds?/i;
  const match = log.match(timeRegex);

  if (match) {
    const label = match[1].trim();
    const rawSeconds = parseFloat(match[2]);

    if (rawSeconds < 1) {
      const ms = Math.round(rawSeconds * 1000);
      return log.replace(timeRegex, `${label} ${ms} milliseconds`);
    }

    const hours = Math.floor(rawSeconds / 3600);
    const minutes = Math.floor((rawSeconds % 3600) / 60);
    const seconds = Math.floor(rawSeconds % 60);

    let parts = [];
    if (hours) parts.push(`${hours} hour${hours > 1 ? 's' : ''}`);
    if (minutes) parts.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);
    if (seconds || parts.length === 0) parts.push(`${seconds} second${seconds !== 1 ? 's' : ''}`);

    const formatted = parts.join(' ');

    return log.replace(timeRegex, `${label} ${formatted}`);
  }

  return log;
};

/**
 * Immutably updates a nested object at the specified path.
 * @param {object} obj - The object to update
 * @param {string[]} path - Array of keys representing the path
 * @param {any} value - The new value to set
 * @returns {object} New object with the updated value
 */
export const updateNestedObject = (obj, path, value) => {
  if (!path.length) return value;
  const [head, ...rest] = path;
  return {
    ...obj,
    [head]: updateNestedObject(obj?.[head] || {}, rest, value),
  };
};

